/**
 * Shared pageGraph.baseRedirects application (FUDR-14607).
 *
 * Used by:
 * - prodServer: real HTTP 301/302 on cold HTML requests
 * - Site client: in-app path rewrite before route matching
 *
 * Prefix match only, longest `from` wins, one hop (no chains).
 * `status` is returned for HTTP responders; SPA callers may ignore it.
 */

const startsWithSegment = (pathname, base) => {
  if (!base || base === '/') {
    return pathname === '/' || pathname.startsWith('/');
  }
  return pathname === base || pathname.startsWith(`${base}/`);
};

/** QR deep links must stay under /app/scan even when /app → "" is configured. */
const DEFAULT_CARVE_OUTS = ['/app/scan'];

/**
 * @param {string} pathname
 * @param {Array<{ from?: string, to?: string, status?: number, client?: string }>|null} redirects
 * @param {{ client?: 'browser'|'webview'|'any', carveOuts?: string[], homePath?: string }} [options]
 * @returns {{ pathname: string, status: number|null, matched: boolean }}
 */
export const applyPageGraphBaseRedirects = (
  pathname = '',
  redirects = null,
  { client = 'browser', carveOuts = DEFAULT_CARVE_OUTS, homePath = '/' } = {},
) => {
  const pathIn = String(pathname || '') || homePath;
  if (!Array.isArray(redirects) || !redirects.length) {
    return { pathname: pathIn, status: null, matched: false };
  }

  for (let i = 0; i < carveOuts.length; i += 1) {
    if (startsWithSegment(pathIn, carveOuts[i])) {
      return { pathname: pathIn, status: null, matched: false };
    }
  }

  const rules = [...redirects]
    .filter((rule) => rule && rule.from)
    .sort((a, b) => String(b.from).length - String(a.from).length);

  for (let i = 0; i < rules.length; i += 1) {
    const rule = rules[i];
    const ruleClient = rule.client || 'any';
    if (ruleClient !== 'any' && ruleClient !== client) continue;

    const from = String(rule.from);
    if (!startsWithSegment(pathIn, from)) continue;

    const to = rule.to == null ? '' : String(rule.to);
    const rest = pathIn.slice(from.length);
    let next = `${to}${rest}`.replace(/\/{2,}/g, '/') || homePath;
    if (!next.startsWith('/')) next = `/${next}`;

    const status = rule.status === 302 ? 302 : 301;
    return { pathname: next, status, matched: true };
  }

  return { pathname: pathIn, status: null, matched: false };
};

const pathSegments = (value) => String(value == null ? '' : value).split('/').filter(Boolean);

/** Join a surface base path and a page-relative path into an absolute path. */
const joinBasePath = (basePath, relativePath) => {
  const segments = [...pathSegments(basePath), ...pathSegments(relativePath)];
  return segments.length ? `/${segments.join('/')}` : '/';
};

/**
 * Resolve a cold-load pathname against page-level `kind: redirect` rows.
 *
 * baseRedirects are platform prefix rewrites; these are per-page renames the
 * server leaves behind so old/indexed URLs keep working. Returns the HTTP target
 * and status so prodServer can emit a real 301/302 (the SPA `<Redirect>` only
 * covers in-app navigation and cannot carry a status). `redirectStatus` defaults
 * to 301 per contract. `surfaces` omitted means published on both.
 *
 * @param {string} pathname
 * @param {Array<{ path?: string, kind?: string, redirectTo?: string, redirectStatus?: number, surfaces?: string[] }>|null} pages
 * @param {{ basePath?: string, surface?: 'web'|'app' }} [options]
 * @returns {{ to: string, status: number }|null}
 */
export const resolvePageGraphRedirect = (
  pathname = '',
  pages = null,
  { basePath = '', surface = 'web' } = {},
) => {
  const pathIn = String(pathname || '');
  if (!pathIn || !Array.isArray(pages) || !pages.length) return null;

  for (let i = 0; i < pages.length; i += 1) {
    const page = pages[i];
    if (!page || page.kind !== 'redirect' || !page.redirectTo) continue;

    const surfaces = page.surfaces;
    if (Array.isArray(surfaces) && surfaces.length && !surfaces.includes(surface)) continue;

    const from = joinBasePath(basePath, page.path);
    if (from === '/') continue;
    if (pathIn !== from && !pathIn.startsWith(`${from}/`)) continue;

    // Carry any tail so a renamed parent keeps its children (/locations/X -> /our-stores/X).
    const tail = pathIn.slice(from.length);
    const to = `${joinBasePath(basePath, page.redirectTo)}${tail}`.replace(/\/{2,}/g, '/') || '/';
    if (to === pathIn) return null;
    return { to, status: page.redirectStatus === 302 ? 302 : 301 };
  }

  return null;
};

export default applyPageGraphBaseRedirects;
